/**
 * Cross-refresh session recovery via IndexedDB.
 *
 * Saves and restores the working session so a page refresh doesn't lose
 * work. Heavy work (per-object base64, JSON encode, IDB writes) runs in
 * the persistence worker; this module is a thin main-thread wrapper.
 *
 * Undo stacks are intentionally NOT persisted — they contain full mesh
 * geometry and quickly exceed storage budgets. Session restore brings
 * back scene state only.
 */

import type { AppState } from "../store.ts";
import { onSaveError } from "./sceneCache.ts";
import { clearSession } from "./sceneDB.ts";
import {
  applySceneDocument,
  captureSessionDocument,
  type SessionMetadata,
} from "./sceneDocument.ts";
import { loadSessionCache, saveSessionCache } from "./sceneDocumentIO.ts";
import type { RawSessionDocument } from "./types.ts";

/**
 * Save the current session.
 *
 * `libraryItemId` identifies which cloud scene this session is backing up;
 * the open-from-library flow uses it to decide whether to silently load a
 * newer local autosave in place of the cloud version. Pass `null` for
 * blank sessions not yet associated with a library item.
 *
 * The live camera position is intentionally excluded — on restore we fall
 * back to the first saved bookmark or the store defaults. Intentional
 * `.pr` saves still include camera state.
 */
export async function saveSessionState(
  state: AppState,
  libraryItemId: string | null,
): Promise<void> {
  try {
    const metadata: SessionMetadata = {
      blendFileName: [...state.blendFiles.values()][0]?.fileName ?? null,
      blendFileHash: [...state.blendFiles.values()][0]?.hash ?? null,
      currentSceneFileName: state.currentSceneFileName,
      sceneDirty: state.sceneDirty,
      libraryItemId,
    };
    const raw = captureSessionDocument(state, metadata);
    raw.cameraPosition = null;
    raw.cameraTarget = null;
    await saveSessionCache(raw);
  } catch (e) {
    onSaveError?.(e);
  }
}

/**
 * Load the saved session.
 *
 * Returns both the decoded `RawSessionDocument` (used by `applySessionState`)
 * and the original JSON string (used by crash-recovery upload paths that
 * forward the stored bytes verbatim).
 */
export async function loadSessionState(): Promise<{
  session: RawSessionDocument | null;
  json: string | null;
}> {
  try {
    const { raw, json } = await loadSessionCache();
    return { session: raw, json };
  } catch {
    return { session: null, json: null };
  }
}

export async function clearSessionState(): Promise<void> {
  try {
    await clearSession();
  } catch {
    // ignore
  }
}

/**
 * Apply a saved session to the store.
 *
 * Calls `applySceneDocument` directly — no JSON round-trip, no second
 * decode pass. Restores session metadata (`currentSceneFileName`,
 * `sceneDirty`) on top, plus the camera-fallback to the first bookmark
 * since live camera state was excluded from the cache.
 */
export function applySessionState(
  session: RawSessionDocument,
  state: AppState,
): Partial<AppState> {
  // Session restore tolerates dropped objects silently — they were already
  // present in the running app, so a validation failure here is a quirk of
  // structured-clone, not user-facing data loss.
  const { patch } = applySceneDocument(session, state);

  if (!patch.cameraPosition && patch.cameraBookmarks?.length) {
    const bk = patch.cameraBookmarks[0];
    patch.cameraPosition = bk.position;
    patch.cameraTarget = bk.target;
    patch.focalLength = bk.focalLength;
  }

  patch.currentSceneFileName = session.currentSceneFileName;
  patch.sceneDirty = session.sceneDirty;

  return patch;
}
